import { useEffect, useState, type FormEvent } from 'react';
import { Field, Logo, Note, Panel, ShieldIcon, Spinner } from '@wyocspay/ui';
import { ApiError, api, setToken } from './api.js';
import { loadIdentity, type DeviceIdentity } from './device.js';

/**
 * Admin sign-in.
 *
 * Spec: "device identity -> user authentication -> passkey/MFA -> short-lived
 * access token -> controlled renewal -> server-side revocation."
 *
 * The device identity still comes first; it is simply no longer this page's
 * job. A TLS client certificate, installed in the operating system keychain and
 * verified by nginx against the platform's own CA, is presented on the
 * connection before a single byte of this application is served. By the time
 * anyone sees this form the machine has already been established, which is why
 * the form asks for nothing but an email and a password.
 *
 * There used to be an enrolment screen here: a one-time code that registered a
 * browser-held Ed25519 key. It is gone, and it went for the reason certificates
 * exist. The key lived in IndexedDB, IndexedDB is not durable storage, and
 * clearing site data in Safari destroyed the owner's only admin credential.
 * A credential that a routine browser housekeeping action can delete is not a
 * credential. The endpoint behind that screen still exists in the API,
 * unreferenced from here - see the note on `POST /auth/device-enrolment`.
 */
export function Login({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  /**
   * A key left over from before certificates, if this browser has one.
   *
   * Read silently and never shown. It is sent with the sign-in so a browser
   * enrolled the old way keeps its device binding through the changeover, and
   * so that the signing key it holds stays usable for privileged commands.
   * Nothing here creates one.
   */
  const [legacyIdentity, setLegacyIdentity] = useState<DeviceIdentity | null>(null);

  useEffect(() => {
    void (async () => {
      setLegacyIdentity(await loadIdentity());
      setChecking(false);
    })();
  }, []);

  async function signIn(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.login(email, password, legacyIdentity?.deviceId ?? null);
      setToken(result.token);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Входът е неуспешен.');
    } finally {
      setBusy(false);
    }
  }

  if (checking) {
    return <Centre><Spinner label="Зареждане…" /></Centre>;
  }

  return (
    <Centre>
      <Panel title="Вход за администратор">
        <form onSubmit={signIn}>
          <Field label="Имейл">
            <input className="wc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />
          </Field>
          <Field label="Парола">
            <input className="wc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          </Field>

          {error ? <div style={{ marginBottom: 14 }}><Note tone="danger">{error}</Note></div> : null}

          <button type="submit" className="wc-btn" style={{ width: '100%' }} disabled={busy}>
            <ShieldIcon size={14} /> {busy ? 'Влизане…' : 'Вход'}
          </button>
        </form>

        {/*
          * No hint about how to obtain a certificate.
          *
          * Anyone who reaches this form has already presented one, so the person
          * who is meant to be here needs no instructions. Naming the route by
          * which credentials are issued, or an administrator's email beside it,
          * would only ever help somebody who should not be reading it.
          */}
      </Panel>
    </Centre>
  );
}


function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'var(--wc-blue-header)', color: '#fff', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Logo height={56} />
        </div>
      </header>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 460 }}>{children}</div>
      </main>
    </div>
  );
}
